/**
 * Réglage « Réduire les animations » (Paramètres → Affichage, 05/11/2026).
 * Appliqué par une classe sur <html> (voir index.css) : les animations et
 * transitions sont neutralisées. Mémorisé localement pour s'appliquer dès le
 * chargement, avant que le serveur ait répondu ; la valeur serveur fait foi.
 */
export const CLE_REDUIRE_ANIMATIONS = "gively_reduire_animations";
export const CLASSE_REDUIRE_ANIMATIONS = "reduire-animations";

export function appliquerReduireAnimations(actif, doc = typeof document !== "undefined" ? document : null) {
  if (doc && doc.documentElement) {
    doc.documentElement.classList.toggle(CLASSE_REDUIRE_ANIMATIONS, !!actif);
  }
  try {
    if (actif) window.localStorage.setItem(CLE_REDUIRE_ANIMATIONS, "1");
    else window.localStorage.removeItem(CLE_REDUIRE_ANIMATIONS);
  } catch { /* stockage indisponible : le réglage serveur s'applique au prochain chargement */ }
  return !!actif;
}

/** Au démarrage : la dernière valeur connue sur cet appareil. */
export function appliquerReduireAnimationsMemorisee(doc = typeof document !== "undefined" ? document : null) {
  let actif = false;
  try { actif = window.localStorage.getItem(CLE_REDUIRE_ANIMATIONS) === "1"; } catch { actif = false; }
  if (doc && doc.documentElement && actif) doc.documentElement.classList.add(CLASSE_REDUIRE_ANIMATIONS);
  return actif;
}

/**
 * Thème (Paramètres → Affichage, 05/11/2026 — demande de Lucas : « Thème sombre,
 * Thème de l'appareil, c'est possible ? ») : « clair », « sombre » ou « appareil »
 * (suit le réglage du téléphone et change en direct quand il bascule). Appliqué par la
 * classe `dark` sur <html> (tailwind darkMode: "class" + couche sombre de index.css),
 * mémorisé localement pour s'appliquer AVANT le premier rendu (script en tête de
 * public/index.html) ; la valeur serveur fait foi dès la connexion (SynchroAffichage).
 */
export const CLE_THEME = "gively_theme";
export const THEMES = ["clair", "sombre", "appareil"];
export const COULEUR_BARRE = { clair: "#ffffff", sombre: "#000000" };

function requeteSombre(win) {
  try {
    return win && typeof win.matchMedia === "function" ? win.matchMedia("(prefers-color-scheme: dark)") : null;
  } catch { return null; }
}

/** Le thème réellement affiché : « clair » ou « sombre ». */
export function themeEffectif(theme, win = typeof window !== "undefined" ? window : null) {
  if (theme === "sombre" || theme === "clair") return theme;
  const mq = requeteSombre(win);
  return mq && mq.matches ? "sombre" : "clair";
}

/** Le dernier choix connu sur cet appareil (sinon « appareil »). */
export function themeMemorise(win = typeof window !== "undefined" ? window : null) {
  try {
    const t = win.localStorage.getItem(CLE_THEME);
    return THEMES.includes(t) ? t : "appareil";
  } catch { return "appareil"; }
}

/** Barre d'état de l'application (Android/iOS) assortie au thème. Sans effet sur le web. */
export function barreNative(effectif, win = typeof window !== "undefined" ? window : null) {
  try {
    const cap = win && win.Capacitor;
    if (!cap || typeof cap.isNativePlatform !== "function" || !cap.isNativePlatform()) return;
    import("@capacitor/status-bar").then(({ StatusBar, Style }) => {
      StatusBar.setStyle({ style: effectif === "sombre" ? Style.Dark : Style.Light }).catch(() => {});
      StatusBar.setBackgroundColor({ color: COULEUR_BARRE[effectif] }).catch(() => {});
    }).catch(() => {});
  } catch { /* plugin absent */ }
}

function peindre(effectif, doc, win) {
  const racine = doc && doc.documentElement;
  if (!racine) return;
  racine.classList.toggle("dark", effectif === "sombre");
  const meta = doc.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", COULEUR_BARRE[effectif]);
  barreNative(effectif, win);
}

let ecouteAppareil = null;

export function appliquerTheme(theme, doc = typeof document !== "undefined" ? document : null,
                               win = typeof window !== "undefined" ? window : null) {
  const choix = THEMES.includes(theme) ? theme : "appareil";
  const effectif = themeEffectif(choix, win);
  peindre(effectif, doc, win);
  try { win.localStorage.setItem(CLE_THEME, choix); } catch { /* stockage indisponible : la valeur serveur revient au prochain chargement */ }
  if (ecouteAppareil) {
    try { ecouteAppareil.mq.removeEventListener("change", ecouteAppareil.fn); } catch { /* ancien navigateur */ }
    ecouteAppareil = null;
  }
  // « Thème de l'appareil » : on suit les bascules du téléphone, en direct.
  const mq = requeteSombre(win);
  if (choix === "appareil" && mq && typeof mq.addEventListener === "function") {
    const fn = () => peindre(themeEffectif("appareil", win), doc, win);
    mq.addEventListener("change", fn);
    ecouteAppareil = { mq, fn };
  }
  return effectif;
}

/** Au démarrage : le dernier choix mémorisé (ou le réglage de l'appareil). */
export function appliquerThemeMemorise(doc = typeof document !== "undefined" ? document : null,
                                       win = typeof window !== "undefined" ? window : null) {
  return appliquerTheme(themeMemorise(win), doc, win);
}
